import { WidgetProps } from '@highimpact-front/remote-widget-core';
import { JSONSerializable } from '@highimpact-frontend/api-types';
import { PermissionDecorator } from '@highimpact/react-decorators';
import { forwardRef, LegacyRef } from 'react';

import { GenericErrorBoundary } from '@highimpact/dashboard-ui';
import {
  useDashboardHost,
  useDashboardScope,
} from '../../../../context/DashboardHostContext';
import { buildRemoteManifestUrl } from '../../../../internal/remote/buildRemoteManifestUrl';
import type { WidgetRuntimeApi } from '../../../../remote/WidgetRuntimeApi';
import { useDashboardLibraryDispatch } from '../../../../runtime/DashboardLibraryProvider';
import { useWidgetRuntimeApi } from '../../../../runtime/useWidgetRuntimeApi';
import { isRemoteWidgetAwaitingConfiguration } from '../../../../state/dashboard-element-operations';
import {
  setDeleteWidgetConfirmation,
  setEditing,
  setWidgetConfigPanelState,
} from '../../../../state/dashboard/slice';
import NameHandlerComponent from '../../../NameHandlerComponent';
import UnconfiguredWidgetPlaceholder from '../../commons/UnconfiguredWidgetPlaceholder';
import Widget from '../../commons/Widget';
import { WidgetToolbar } from '../../commons/WidgetToolbar';
import { WidgetDragHandle } from '../../commons/WidgetDragHandle';

interface RemoteWidgetSpecificProps {
  name: string;
  specificWidgetType: string;
  url: string;
  additionalProps?: Record<string, JSONSerializable>;
}

export type RemoteWidgetProps = WidgetProps & RemoteWidgetSpecificProps;

interface RemoteWidgetHelperProps extends RemoteWidgetSpecificProps {
  readonly widgetRuntimeApi: WidgetRuntimeApi;
}

function RemoteWidgetHelper({
  specificWidgetType,
  url,
  additionalProps,
  widgetRuntimeApi,
  ...rest
}: RemoteWidgetHelperProps) {
  const { renderRemoteWidget } = useDashboardHost();

  const remoteUrl = buildRemoteManifestUrl(url, specificWidgetType);

  return (
    <>
      {renderRemoteWidget({
        remoteName: specificWidgetType,
        remoteUrl,
        moduleName: 'Widget',
        props: {
          name: rest.name,
          updateAdditionalProps: () => {
            throw new Error('updateAdditionalProps is not implemented');
          },
          updateDisplayerSettings: () => {
            throw new Error('updateDisplayerSettings is not implemented');
          },
          additionalProps,
          specificWidgetType,
          url,
          widgetRuntimeApi,
        },
      })}
    </>
  );
}

function RemoteWidget(
  props: RemoteWidgetProps,
  ref: LegacyRef<HTMLDivElement>
): JSX.Element {
  const dispatch = useDashboardLibraryDispatch();
  const { project_id, site_id } = useDashboardScope();
  const widgetRuntimeApi = useWidgetRuntimeApi();

  const gridData = widgetRuntimeApi.getGridForElement(props.dashboardElementId);
  const widget = widgetRuntimeApi.getWidget(props.widget_id);

  if (!gridData) {
    return (
      <div>
        Les informations du tableau de bord n'ont pas pu être récupérées
      </div>
    );
  }

  const { type: gridType, id: gridId } = gridData;
  const isAwaitingConfiguration = isRemoteWidgetAwaitingConfiguration(
    props.additionalProps
  );

  const remoteUrl = buildRemoteManifestUrl(props.url, props.specificWidgetType);
  const remoteName = props.specificWidgetType;
  const moduleName = 'exportFn';

  return (
    <Widget
      style={{ width: '100%', height: '100%' }}
      ref={ref}
      {...props}
      className="remotewidget"
      {...(!isAwaitingConfiguration
        ? {
            'data-remote-url': remoteUrl,
            'data-remote-name': remoteName,
            'data-module-name': moduleName,
          }
        : {})}
    >
      <GenericErrorBoundary>
        <PermissionDecorator
          item="dashboard_nav_bar_widget"
          project_id={project_id ?? undefined}
          site_id={site_id ?? undefined}
          extra={{ dashboard_id: gridId }}
        >
          <div className="widget-nav">
            <p style={{ display: 'flex' }}>
              {!props.dashboardOnlyMode && (
                <PermissionDecorator item="dashboard_move_widget">
                  <WidgetDragHandle />
                </PermissionDecorator>
              )}
              <PermissionDecorator item="dashboard_rename_widget">
                <NameHandlerComponent
                  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain, @typescript-eslint/no-non-null-assertion
                  widgetId={widget?.elementData.id!}
                  dashboardElementId={props.widget_id}
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  dashboardId={gridData.id!}
                  dashboardType={gridType}
                />
              </PermissionDecorator>
            </p>

            {!props.dashboardOnlyMode && (
              <div className="nav-icon">
                <WidgetToolbar
                  dashboardElementId={props.dashboardElementId}
                  dashboardId={gridId}
                  // TODO: replace
                  platformActions={{
                    edit: props.is_editable
                      ? {
                          visible: true,
                          tooltipText: 'Panneau de configuration',
                          permissionItem: 'dashboard_configure_widget',
                          onClick: () =>
                            isAwaitingConfiguration
                              ? dispatch(
                                  setWidgetConfigPanelState({
                                    state: 'Configuring',
                                    elementType: 'remotewidget',
                                    gridId: gridId,
                                    gridType: gridType,
                                    widgetId: props.dashboardElementId,
                                  })
                                )
                              : dispatch(
                                  setEditing({
                                    state: 'EditingWithoutDisplayer',
                                    widgetId: props.widget_id,
                                    gridId: gridId,
                                    gridType: gridType,
                                  })
                                ),
                        }
                      : undefined,
                    delete: !props.non_removable
                      ? {
                          visible: true,
                          tooltipText: "Supprimer l'élément",
                          permissionItem: 'dashboard_delete_widget',
                          onClick: () =>
                            dispatch(
                              setDeleteWidgetConfirmation({
                                state: 'open',
                                dashboardElementId: props.dashboardElementId,
                                widgetName: props.name,
                              })
                            ),
                        }
                      : undefined,
                  }}
                />
              </div>
            )}
          </div>
        </PermissionDecorator>
        {isAwaitingConfiguration ? (
          <UnconfiguredWidgetPlaceholder
            message="Ce widget est en attente de configuration"
            gridId={gridId}
            gridType={gridType}
            widgetId={props.dashboardElementId}
            elementType="remotewidget"
          />
        ) : (
          <RemoteWidgetHelper {...props} widgetRuntimeApi={widgetRuntimeApi} />
        )}
      </GenericErrorBoundary>
    </Widget>
  );
}

export default forwardRef(RemoteWidget);
